import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useNotificationStore } from '@/stores/notification';
import styles from './NotificationPanel.module.css';

const TYPE_ICON: Record<string, string> = {
  tenant_created: '\uD83C\uDFE2',
  billing_unpaid: '\uD83D\uDCB3',
  support_ticket: '\uD83C\uDFAB',
  lead_new: '\uD83D\uDCCB',
  contact_new: '\uD83D\uDCE9',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return '\u4ECA';
  if (min < 60) return `${min}\u5206\u524D`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}\u6642\u9593\u524D`;
  const day = Math.floor(hr / 24);
  return `${day}\u65E5\u524D`;
}

export function NotificationPanel() {
  const { notifications, isOpen, close, markRead, markAllRead, remove, unreadCount, filter, setFilter } = useNotificationStore();
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, close]);

  if (!isOpen) return null;

  const filtered = filter === 'unread' ? notifications.filter(n => !n.is_read) : notifications;

  return (
    <div className={styles.panel} ref={panelRef}>
      <div className={styles.header}>
        <span className={styles.headerTitle}>{'\u901A\u77E5'}</span>
        {unreadCount > 0 && (
          <button className={styles.readAllBtn} onClick={() => markAllRead()}>
            {'\u3059\u3079\u3066\u65E2\u8AAD'}
          </button>
        )}
      </div>
      <div className={styles.filterBar}>
        <button
          className={`${styles.filterBtn} ${filter === 'all' ? styles.filterActive : ''}`}
          onClick={() => setFilter('all')}
        >{'\u3059\u3079\u3066'}</button>
        <button
          className={`${styles.filterBtn} ${filter === 'unread' ? styles.filterActive : ''}`}
          onClick={() => setFilter('unread')}
        >{'\u672A\u8AAD\u306E\u307F'}{unreadCount > 0 && ` (${unreadCount})`}</button>
      </div>
      <div className={styles.list}>
        {filtered.length === 0 ? (
          <div className={styles.empty}>{filter === 'unread' ? '\u672A\u8AAD\u306E\u901A\u77E5\u306F\u3042\u308A\u307E\u305B\u3093' : '\u901A\u77E5\u306F\u3042\u308A\u307E\u305B\u3093'}</div>
        ) : (
          filtered.map((n) => (
            <div
              key={n.id}
              className={`${styles.item} ${n.is_read ? '' : styles.unread}`}
              onClick={() => {
                if (!n.is_read) markRead(n.id);
                if (n.link) navigate(n.link.replace('/console', ''));
                close();
              }}
            >
              <span className={styles.icon}>{TYPE_ICON[n.type] ?? '\uD83D\uDD14'}</span>
              <div className={styles.content}>
                <div className={styles.title}>{n.title}</div>
                {n.body && <div className={styles.body}>{n.body}</div>}
                <div className={styles.time}>{timeAgo(n.created_at)}</div>
              </div>
              <button
                className={styles.deleteBtn}
                title={'\u524A\u9664'}
                onClick={(e) => {
                  e.stopPropagation();
                  remove(n.id);
                }}
              >{'\uD83D\uDDD1'}</button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
